import { memo } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import { useBuilderTranslation } from '@/i18n/hooks/useTypedTranslation';

interface SelectionLimitsEditorProps {
  minSelections: number | undefined;
  maxSelections: number | undefined;
  maxOptions: number;
  onMinChange: (value: number | undefined) => void;
  onMaxChange: (value: number | undefined) => void;
  title?: string;
  minLabel?: string;
  maxLabel?: string;
  minPlaceholder?: string;
  maxPlaceholder?: string;
  helperText?: string;
  minMin?: number;
}

/**
 * Reusable component for min/max selection limit configuration.
 * Used by MultipleChoiceEditor and RankedChoiceEditor.
 */
export const SelectionLimitsEditor = memo(function SelectionLimitsEditor({
  minSelections,
  maxSelections,
  maxOptions,
  onMinChange,
  onMaxChange,
  title,
  minLabel,
  maxLabel,
  minPlaceholder,
  maxPlaceholder,
  helperText,
  minMin = 0,
}: SelectionLimitsEditorProps) {
  const { t } = useBuilderTranslation();

  // Use translations as defaults if not provided
  const displayTitle = title || t('selectionLimits.title');
  const displayMinLabel = minLabel || t('selectionLimits.min.label');
  const displayMaxLabel = maxLabel || t('selectionLimits.max.label');
  const displayMinPlaceholder = minPlaceholder || t('selectionLimits.min.placeholder');
  const displayMaxPlaceholder = maxPlaceholder || t('selectionLimits.max.placeholder');

  return (
    <Box>
      <Typography variant="body2" sx={{ fontWeight: 500, mb: 2 }}>
        {displayTitle}
      </Typography>
      <Box
        sx={{
          display: 'grid',
          gap: 2,
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
        }}
      >
        <TextField
          label={displayMinLabel}
          type="number"
          fullWidth
          slotProps={{
            htmlInput: { min: minMin, max: maxOptions },
          }}
          value={minSelections ?? ''}
          onChange={(e) =>
            onMinChange(e.target.value ? Number(e.target.value) : undefined)
          }
          placeholder={displayMinPlaceholder}
        />
        <TextField
          label={displayMaxLabel}
          type="number"
          fullWidth
          slotProps={{
            htmlInput: { min: 1, max: maxOptions },
          }}
          value={maxSelections ?? ''}
          onChange={(e) =>
            onMaxChange(e.target.value ? Number(e.target.value) : undefined)
          }
          placeholder={displayMaxPlaceholder}
        />
      </Box>
      {helperText && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          {helperText}
        </Typography>
      )}
    </Box>
  );
});

export default SelectionLimitsEditor;
